import React from 'react';
import { modalPathFor, trainerPaymentPathFor } from 'config/paths';
import { billCycleLengths,  StripeModal } from 'views/Conversion';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Trans, withTranslation } from 'react-i18next';
import { trainerRecordSelector, userRecordSelector, trainerLocalizedPricingSelector } from 'redux/selectors';
import * as _ from 'lib/utilities';
import { FULL, NUTRITION, TPAYWALL, TRAINING } from 'config/settings';
import { User } from 'lib/classes';
import { initStripeTrainerSub, updateTrainerSub, loadTrainerLocalizedPricing } from 'redux/actions';
import { SlashedPrice } from './Utilities';

const signupFirstSuffix = '/signup_required';
export const signupFirstPathFor = pathname => modalPathFor(signupFirstSuffix,pathname);

export const baseClients = 5;

const getSummaryFeatures = (planType,cliDols,t) => {
    const arr = [];
    if(planType === NUTRITION) {
        arr.push(t("customizable meal planner"));
    } else if(planType === TRAINING) {
        arr.push(t("custom workout routines"));
    } else {
        arr.push(t("all nutrition features"));
        arr.push(t("all training features"));
    }
    if(planType !== FULL) {
        let str = `${t("iOS/Android/web app access for clients")}`;
        if(planType === NUTRITION) {
            str = `${str} (${t('optional')})`
        }
        arr.push(str);
    }
    arr.push(<Trans i18nKey="x clients y dollars" >{`${baseClients}`}{cliDols}</Trans>);

    return arr;
}

const prodIsCurrent = ({ planType, frequency, user }) => {
    if(user.hasActiveTrainerSub() && !user.trainerSubIsOldPrice()) {
        return `${planType}-${frequency}` === `${user.activeTrainerPlanType()}-${user.trainerSubFreq()}`;
    }
    return false;
}

const prodIsSelectable = ({ planType, frequency, user, context }) => {
    if(context === TPAYWALL) {
        return (planType !== user.activeTrainerPlanType());
    } else {
        return !prodIsCurrent({ planType, frequency, user });
    }
}

const getFallbackFreqData = (planType, frequency) => {
    const planNames = {
        [NUTRITION]: "Nutrition Only",
        [FULL]: "Full Package",
        [TRAINING]: "Training Only"
    };
    const shortNames = {
        [NUTRITION]: "Nutrition",
        [FULL]: "Full",
        [TRAINING]: "Training"
    };

    return {
        name: planNames[planType] || "",
        shortName: shortNames[planType] || "",
        amount: "",
        monthlyAmount: "",
        months: billCycleLengths[frequency] || 1,
        dAmount: null,
        dLength: null,
        cliDols: "",
        dCliDols: null,
        discount: null,
        priceId: null
    };
};

const getProducts = (t,user,frequency,context,successHandler,pricingData) => {
    const curPlan = user.activeTrainerPlanType();
    const trainerType = user.getTrainerType();
    const isUpgrade = user.hasActiveTrainerSub() && user.canManageOwnAccount();
    const isTrialing = user.isTrainerTrialing();
    const currency = (pricingData && pricingData.currency) || 'USD';

    let planTypes = [NUTRITION, FULL, TRAINING];
    if(curPlan && context === TPAYWALL && trainerType !== User.TRAINER_TYPES.pdf) {
        planTypes = [curPlan, FULL];
    }

    const prods = planTypes.map(planType => {
        let freqData;

        if(pricingData && pricingData[planType] && pricingData[planType][frequency]) {
            freqData = pricingData[planType][frequency];
        } else {
            freqData = getFallbackFreqData(planType, frequency);
        }

        const isCurrent = prodIsCurrent({ planType, frequency, user });

        let cliDolsStr = freqData.cliDols;
        if(user.isFromDiscountedAffiliate() && freqData.dCliDols) {
            cliDolsStr = <SlashedPrice oldPrice={freqData.cliDols} discountPrice={freqData.dCliDols} dark />;
        }

        return {
            name: t(freqData.name),
            amount: freqData.amount,
            monthlyAmount: freqData.monthlyAmount,
            months: freqData.months,
            dAmount: freqData.dAmount,
            dLength: freqData.dLength,
            topPriceStr: user.isFromDiscountedAffiliate() && freqData.dAmount ?
                <React.Fragment><SlashedPrice oldPrice={freqData.monthlyAmount} discountPrice={freqData.dAmount} dark />{`/${t('month abbr')}`}</React.Fragment> :
                `${freqData.monthlyAmount}/${t('month abbr')}`,
            bottomPriceStr: t("amount currency every X months", { amount: freqData.amount, currency: currency, months: freqData.months } ),
            moneyBackString: (user && user.hadTrainerTrial()) ? '' : (user.isFromTrainerMailer() ? t('30-day free trial') : t("14-day free trial")),
            primary: trainerType === User.TRAINER_TYPES.pdf ? planType === NUTRITION : planType === FULL,
            selectable: prodIsSelectable({ planType, frequency, user, context }),
            isUpgrade: isUpgrade,
            isTrialing,
            planType,
            frequency,
            forTrainers: true,
            discount: freqData.discount,
            currency: currency,
            featureSummary: getSummaryFeatures(planType,cliDolsStr,t),
            getPlanParams: () => ({ planType, frequency }),
            initiateCheckout: () => {},
            hasTrial: !(user && user.hadTrainerTrial()),
            shortName: freqData.shortName,
            extraClientPrice: cliDolsStr,
            onSuccess: successHandler,
            isCurrent,
            priceId: freqData.priceId
        }
    });

    return prods;
}

export const withStore = (initialSelect,coupon=null,allFreqs=false) => (Component) => {

    const mapStateToProps = state => ({
        user: trainerRecordSelector(state) || userRecordSelector(state) || User.defaultUser(),
        trainerLocalizedPricing: trainerLocalizedPricingSelector(state)
    })

    const mapDispatchToProps = dispatch => ({
        initStripeAction: data => dispatch(initStripeTrainerSub(data)),
        updatePlan: data => dispatch(updateTrainerSub(data)),
        loadPricing: () => dispatch(loadTrainerLocalizedPricing())
    })

    class Store extends React.Component {

        //products have: initiateCheckout(), topPriceStr, bottomPriceStr, moneyBackString, primary
        constructor(props) {
            super(props);

            this.paymentModalOpen = false;
            this.buildProducts = this.buildProducts.bind(this);
            this.handlePaymentModalClose = this.handlePaymentModalClose.bind(this);
            this.initStripePayment = this.initStripePayment.bind(this);
            this.getTrainerType = this.getTrainerType.bind(this);
            this.storeContext = props.match.params && props.match.params.context;
            const initFreq = 'monthly';

            // Load pricing data
            props.loadPricing();

            //build products
            const products = this.buildProducts(initFreq);

            this.state = { frequency: initFreq, products, selectedProduct: (initialSelect ? (products[1] || products[0]) : null) };
            this.mounted = true;
        }

        getTrainerType() {
            const { user } = this.props;
            return user.getTrainerType();
        }

        buildProducts(frequency) {
            const { t, user, updatePlan, history, location: { pathname }, trainerLocalizedPricing } = this.props;
            let products;
            if(allFreqs) {
                products = _.flatMap(['quarterly','monthly'],freq => getProducts(t,user,freq,this.storeContext,this.successHandler,trainerLocalizedPricing));
            } else {
                products = getProducts(t,user,frequency,this.storeContext,this.successHandler,trainerLocalizedPricing);
            }

            products.forEach( product => {
                const planParams = { planType: product.planType, frequency: product.frequency };
                if(coupon) {
                    planParams.coupon = coupon;
                }
                product.getPlanParams = () => planParams;

                product.initiateCheckout = () => {
                    if(_.isBlank(user.email) || !user.isTrainer()) {
                        history.push(signupFirstPathFor(pathname))
                    }  else {
                        if(product.isUpgrade) {
                            return updatePlan({ ...product.getPlanParams() });
                        } else {
                            this.initStripePayment(product);
                        }
                    }
                }
            });

            return products;
        }

        initStripePayment(product) {
            if(!this.paymentModalOpen) {
                this.paymentModalOpen = true;
                this.selectProduct(product);
                this.props.history.push(trainerPaymentPathFor(this.props.match.params.context));
            }
        }

        selectProduct = (product) =>  this.setState({ selectedProduct: product });

        setFrequency = frequency => {
            this.setState({ frequency })
        }

        handlePaymentModalClose() {
            this.paymentModalOpen = false;
        }

        componentDidUpdate(prevProps,prevState) {
            if(prevProps.t !== this.props.t || this.state.frequency !== prevState.frequency || prevProps.trainerLocalizedPricing !== this.props.trainerLocalizedPricing) {
                this.setState({ products: this.buildProducts(this.state.frequency)});
            }
        }

        successHandler = () => {
            const { successPath, history } = this.props;
            history.replace(successPath);
        }

        render() {
            const modal = (this.state.selectedProduct && 
                (<StripeModal 
                    closeCallback={this.handlePaymentModalClose} 
                    product={this.state.selectedProduct} 
                    t={this.props.t}
                    i18n={this.props.i18n}
                    submitAction={this.props.initStripeAction}
                    initProSuccess={this.state.selectedProduct.onSuccess}
                    user={this.props.user}
                    disallowNonOwner={true}
                    path={trainerPaymentPathFor(this.props.match.params.context)}
                />)
            )

            return (
                <Component 
                    {...this.props} 
                    selectProduct={this.selectProduct}
                    initProSuccess={this.successHandler}
                    frequency={this.state.frequency}
                    setFrequency={this.setFrequency} 
                    products={this.state.products} 
                    selectedProduct={this.state.selectedProduct}
                    modal={modal} 
                />
            )
        }
    }

    return connect(mapStateToProps,mapDispatchToProps)(withRouter(withTranslation()(Store)));
}